import { convexQuery } from '@convex-dev/react-query'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  IconOffice2Outline18,
  IconSquareActivityChartOutline18,
  IconUsersOutline18,
} from 'nucleo-ui-outline-18'
import { type ComponentType, useState } from 'react'
import { ActivationFunnelChart } from '~/components/admin/activation-funnel-chart'
import { FeatureUsageChart } from '~/components/admin/feature-usage-chart'
import { GrowthChart } from '~/components/admin/growth-chart'
import { PageHeader } from '~/components/ui/page-header'
import { SegmentedControl, SegmentedControlItem } from '~/components/ui/segmented-control'
import { Spinner } from '~/components/ui/spinner'
import { titleFor } from '~/lib/route-title'
import { api } from '~convex/_generated/api'

export const Route = createFileRoute('/admin/')({
  component: AdminDashboard,
  head: () => ({ meta: [{ title: titleFor(msg`Admin`) }] }),
})

type StatTile = {
  to: '/admin/workspaces' | '/admin/users' | '/admin/activity'
  icon: ComponentType<{ className?: string }>
  label: string
  value: number
  hint?: string
}

function AdminDashboard() {
  const { t, i18n } = useLingui()
  const [growthDays, setGrowthDays] = useState<'30' | '90'>('30')
  const [funnelDays, setFunnelDays] = useState<'all' | '30' | '90'>('all')
  const { data: stats } = useQuery(convexQuery(api.admin.dashboardStats, {}))
  const { data: usage } = useQuery(convexQuery(api.adminMetrics.usage, {}))
  const { data: growth } = useQuery(
    convexQuery(api.adminMetrics.growthSeries, { days: growthDays === '90' ? 90 : 30 })
  )
  const { data: funnel } = useQuery(
    convexQuery(
      api.adminMetrics.activationFunnel,
      funnelDays === 'all' ? {} : { days: funnelDays === '90' ? 90 : 30 }
    )
  )

  if (stats === undefined) {
    return (
      <div className="flex h-full flex-col">
        <PageHeader meta={t`Cross-workspace admin surface`} title={t`Admin`} />
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      </div>
    )
  }

  const tiles: readonly StatTile[] = [
    {
      hint:
        stats.newWorkspacesThisWeek > 0
          ? t`+${i18n.number(stats.newWorkspacesThisWeek)} this week`
          : undefined,
      icon: IconOffice2Outline18,
      label: t`Workspaces`,
      to: '/admin/workspaces',
      value: stats.workspaceCount,
    },
    {
      hint:
        stats.newUsersThisWeek > 0
          ? t`+${i18n.number(stats.newUsersThisWeek)} this week`
          : undefined,
      icon: IconUsersOutline18,
      label: t`Users`,
      to: '/admin/users',
      value: stats.userCount,
    },
    {
      hint: t`wrote or signed in`,
      icon: IconSquareActivityChartOutline18,
      label: t`Active users (7d)`,
      to: '/admin/activity',
      value: usage?.activeUsers.d7 ?? 0,
    },
    {
      hint: t`with write activity`,
      icon: IconSquareActivityChartOutline18,
      label: t`Active workspaces (7d)`,
      to: '/admin/activity',
      value: usage?.activeWorkspaces.d7 ?? 0,
    },
  ]

  return (
    <div className="flex h-full flex-col">
      <PageHeader meta={t`Cross-workspace admin surface`} title={t`Admin`} />
      <div className="min-h-0 flex-1 space-y-8 overflow-y-auto px-5 pb-10">
        <section>
          <h2 className="pb-3 font-medium text-muted-foreground text-sm">
            <Trans>Totals</Trans>
          </h2>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {tiles.map(({ to, icon: Icon, label, value, hint }, index) => (
              <Link
                className="group rounded-xl border border-border bg-card p-5 transition hover:border-foreground/20 hover:shadow-sm"
                key={`${to}-${label}-${index}`}
                to={to}
              >
                <Icon className="size-5 text-muted-foreground transition group-hover:text-foreground" />
                <p className="mt-3 font-semibold text-2xl tracking-tight">{i18n.number(value)}</p>
                <p className="mt-1 text-muted-foreground text-sm">{label}</p>
                {hint ? <p className="mt-1 text-muted-foreground/80 text-xs">{hint}</p> : null}
              </Link>
            ))}
          </div>
        </section>

        <section>
          <div className="flex flex-wrap items-center justify-between gap-2 pb-3">
            <h2 className="font-medium text-muted-foreground text-sm">
              <Trans>Growth</Trans>
            </h2>
            <SegmentedControl onValueChange={(next) => setGrowthDays(next)} value={growthDays}>
              <SegmentedControlItem value="30">{t`30 days`}</SegmentedControlItem>
              <SegmentedControlItem value="90">{t`90 days`}</SegmentedControlItem>
            </SegmentedControl>
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            {growth === undefined ? (
              <div className="flex h-48 items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <GrowthChart newWorkspaces={growth.newWorkspaces} signups={growth.signups} />
            )}
          </div>
        </section>

        <div className="grid gap-8 lg:grid-cols-2">
          <section>
            <div className="flex flex-wrap items-center justify-between gap-2 pb-3">
              <h2 className="font-medium text-muted-foreground text-sm">
                <Trans>Activation funnel</Trans>
              </h2>
              <SegmentedControl onValueChange={(next) => setFunnelDays(next)} value={funnelDays}>
                <SegmentedControlItem value="all">{t`All time`}</SegmentedControlItem>
                <SegmentedControlItem value="30">{t`30 days`}</SegmentedControlItem>
                <SegmentedControlItem value="90">{t`90 days`}</SegmentedControlItem>
              </SegmentedControl>
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              {funnel === undefined ? (
                <div className="flex h-40 items-center justify-center">
                  <Spinner />
                </div>
              ) : (
                <ActivationFunnelChart stages={funnel.stages} />
              )}
            </div>
          </section>

          <section>
            <h2 className="pb-3 font-medium text-muted-foreground text-sm">
              <Trans>Feature usage (30d)</Trans>
            </h2>
            <div className="rounded-xl border border-border bg-card p-5">
              {usage === undefined ? (
                <div className="flex h-40 items-center justify-center">
                  <Spinner />
                </div>
              ) : (
                <FeatureUsageChart featureUsage={usage.featureUsage} />
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
